import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Grid, Typography, Button, CircularProgress, Chip,
  ToggleButtonGroup, ToggleButton, Table, TableBody, TableCell, TableHead, TableRow,
  TableSortLabel, TablePagination,
} from '@mui/material';
import { GpsFixed, Refresh } from '@mui/icons-material';
import axios from 'axios';

const SUGAR_API = 'https://slots.moonraid.online/api/admin/sugar';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('adminToken')}` });

// Оружие: id → эмодзи+имя РОВНО как видит игрок (casino/SUGAR_STRIKE/frontend/src/game/assets.ts).
// Порядок = порядок открытия по обороту (pop → mortar).
const WEAPONS = {
  pop:      { emoji: '🔫', name: 'Pop Blaster' },
  nova:     { emoji: '💠', name: 'Nova Cannon' },
  meteor:   { emoji: '🎯', name: 'Laser Sniper' },
  zap:      { emoji: '⚡', name: 'Zap Coil' },
  bazooka:  { emoji: '🚀', name: 'Bazooka' },
  howitzer: { emoji: '🛰️', name: 'Plasma Howitzer' },
  railgun:  { emoji: '🔩', name: 'Star Railgun' },
  mortar:   { emoji: '🎇', name: 'Doom Mortar' },
};
const WEAPON_ORDER = Object.keys(WEAPONS);
const weaponLabel = (id) => { const w = WEAPONS[id]; return w ? `${w.emoji} ${w.name}` : (id ? `❔ ${id}` : '—'); };
const weaponEmoji = (id) => { const w = WEAPONS[id]; return w ? w.emoji : (id ? '❔' : '—'); };

// Верхние боссы: id → эмодзи+имя РОВНО как в игре (frontend/game/assets.ts TOP_BOSSES).
const TOP_BOSS = {
  kraken: '🐙 KRAKEN', golem: '🧊 ICE GOLEM', demon: '👹 FIRE DEMON',
  mech: '🤖 MECH GUARD', queen: '👽 ALIEN QUEEN',
};
// Метка босса для ленты: 🔺 верхний (по типу) / 🔹 боковой (char, если есть).
const bossLabel = (tier, type) => {
  if (tier === 'top') return `🔺 ${TOP_BOSS[type] || 'Верхний'}`;
  return `🔹 Боковой${type ? ` ${type}` : ''}`;
};

// Подпись текущей сортировки в заголовке таблицы игроков (ключи = SORT_COLS на бэке).
const SORT_LABEL = {
  turnover: 'по обороту', payout: 'по выплатам', rtpLifeAll: 'по RTP', netLife: 'по профиту дома',
  houseReal: 'по реальному профиту',
  shots: 'по выстрелам', avgBet: 'по средней ставке',
  balance: 'по балансу', bosses: 'по боссам', name: 'по нику', updated: 'по активности',
};

// §22.07 МОБИЛЬНАЯ ВЁРСТКА (владелец: «что это за пустые дыры»): карточки в паре имели РАЗНУЮ
// высоту — длинная подпись переносилась на 5 строк, соседняя короткая заканчивалась сразу, и
// под ней зияла дыра. Лечим двумя вещами: height:100% (карточка тянется на высоту строки сетки,
// пустоты между ними нет) и обрезка подписи двумя строками с полным текстом в тултипе.
const Stat = ({ label, value, color, hint }) => (
  <Card sx={{ bgcolor: '#0e1424', border: '1px solid rgba(255,255,255,0.06)', height: '100%' }}>
    <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
      <Typography variant="caption" sx={{ color: '#8fa1c4', display: 'block', lineHeight: 1.25, fontSize: '0.7rem' }}>{label}</Typography>
      <Typography sx={{ color: color || '#fff', fontWeight: 800, lineHeight: 1.25, fontSize: { xs: '1.05rem', md: '1.25rem' }, my: 0.25 }}>{value}</Typography>
      {hint && (
        <Typography variant="caption" title={hint}
          sx={{ color: '#5b6b8c', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden', lineHeight: 1.25, fontSize: '0.66rem' }}>
          {hint}
        </Typography>
      )}
    </CardContent>
  </Card>
);

export default function SugarStrikeAdmin() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today');
  const [players, setPlayers] = useState([]);
  const [live, setLive] = useState(null);
  const [weapons, setWeapons] = useState(null);
  const [bossFeed, setBossFeed] = useState(null);
  // §22.07 ДЖЕКПОТ-ПУЛ (владелец: «хочу видеть всю информацию по пулу»): остаток, кому и
  // сколько выдали за всё время, кто ещё ждёт своей очереди в текущем круге.
  const [poolInfo, setPoolInfo] = useState(null);
  // §06.09.2026 (владелец): «покажи всех, кто играл в ДЕМО за сутки». Демо намеренно
  // не попадает в боевую стату (испортило бы RTP), поэтому у него свой леджер и своя ручка.
  const [demoPlayers, setDemoPlayers] = useState(null);
  const [buffer, setBuffer] = useState(null); // §касса: owed + ожидаемый RTP (effRtp → ~95)
  // §владелец 21.07: таблица игроков — сортировка по ЛЮБОЙ колонке + пагинация. И то и другое
  // считает СЕРВЕР (иначе «топ по RTP» был бы топом текущей страницы, а не всей базы).
  // §22.07 (владелец): по умолчанию — кто ПОСЛЕДНИМ был онлайн, сверху самые свежие.
  // Сортировка по любой другой колонке остаётся, считает её сервер по всей базе.
  const [sort, setSort] = useState('updated');
  const [dir, setDir] = useState('desc');
  const [page, setPage] = useState(0);      // 0-based (TablePagination), на бэк шлём +1
  const [rows, setRows] = useState(10); // §22.07 (владелец): по 10 игроков на страницу
  const [pmeta, setPmeta] = useState(null); // {total, pages, summary}
  const [helpOpen, setHelpOpen] = useState(false);     // §22.07 пояснение к цифрам — свёрнуто
  const [colsHelpOpen, setColsHelpOpen] = useState(false); // §22.07 расшифровка колонок — свёрнута
  const [poolHelpOpen, setPoolHelpOpen] = useState(false); // §22.07 правила пула — свёрнуты
  const [bossHelpOpen, setBossHelpOpen] = useState(false); // §22.07 легенда ленты боссов — свёрнута
  const [bossShow, setBossShow] = useState(15); // §24.07 (владелец): лента по 15, дальше «показать ещё»

  const loadStats = useCallback(async () => {
    try {
      const r = await axios.get(`${SUGAR_API}/stats`, { headers: authHeaders() });
      if (r.data?.success) { setStats(r.data.periods); setBuffer(r.data.buffer || null); }
    } catch (e) { /* тихо */ }
  }, []);

  // §владелец 21.07: игроки за период (по умолчанию сегодня) + сортировка/страница с сервера
  const loadPlayers = useCallback(async () => {
    try {
      const r = await axios.get(`${SUGAR_API}/players`, {
        headers: authHeaders(),
        params: { period, sort, dir, page: page + 1, limit: rows },
      });
      if (r.data?.success) {
        setPlayers(r.data.players);
        setPmeta({ total: r.data.total, pages: r.data.pages, summary: r.data.summary });
      }
    } catch (e) { /* тихо */ }
  }, [period, sort, dir, page, rows]);

  const loadLive = useCallback(async () => {
    try {
      const r = await axios.get(`${SUGAR_API}/live`, { headers: authHeaders() });
      if (r.data?.success) setLive(r.data);
    } catch (e) { /* тихо */ }
  }, []);

  const loadWeapons = useCallback(async () => {
    try {
      const r = await axios.get(`${SUGAR_API}/weapons`, { headers: authHeaders() });
      if (r.data?.success) setWeapons(r.data.periods);
    } catch (e) { /* тихо */ }
  }, []);

  const loadBossFeed = useCallback(async () => {
    try {
      const r = await axios.get(`${SUGAR_API}/boss-feed`, { headers: authHeaders() });
      if (r.data?.success) setBossFeed(r.data);
    } catch (e) { /* тихо */ }
  }, []);

  const loadPool = useCallback(async () => {
    try {
      const r = await axios.get(`${SUGAR_API}/pool`, { headers: authHeaders() });
      if (r.data?.success) setPoolInfo(r.data);
    } catch (e) { /* тихо */ }
  }, []);

  // Демо-игроки за выбранный период (тот же переключатель, что и у боевой таблицы)
  const loadDemoPlayers = useCallback(async () => {
    try {
      const r = await axios.get(`${SUGAR_API}/demo-players`, {
        headers: authHeaders(), params: { period },
      });
      if (r.data?.success) setDemoPlayers(r.data);
    } catch (e) { /* тихо */ }
  }, [period]);

  useEffect(() => {
    (async () => { setLoading(true); await Promise.all([loadStats(), loadWeapons(), loadBossFeed(), loadPool()]); setLoading(false); })();
    const t = setInterval(() => { loadStats(); loadWeapons(); loadBossFeed(); loadPool(); }, 15000);
    return () => clearInterval(t);
  }, [loadStats, loadWeapons, loadBossFeed, loadPool]);

  // Демо — своим циклом: зависит от периода, обновляем реже (демо не про деньги)
  useEffect(() => {
    loadDemoPlayers();
    const t = setInterval(loadDemoPlayers, 20000);
    return () => clearInterval(t);
  }, [loadDemoPlayers]);

  // игроки — отдельно: loadPlayers завязан на период/сортировку/страницу, перезапрос при смене
  // (без общего setLoading, иначе вся страница мигала бы спиннером на каждый клик)
  useEffect(() => {
    loadPlayers();
    const t = setInterval(loadPlayers, 15000);
    return () => clearInterval(t);
  }, [loadPlayers]);

  // смена периода → на первую страницу (иначе можно застрять на пустой 4-й)
  useEffect(() => { setPage(0); }, [period]);

  // клик по заголовку колонки: тот же ключ — разворот, новый — сортируем по нему сверху вниз
  const sortBy = (key) => {
    if (sort === key) setDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSort(key); setDir(key === 'name' ? 'asc' : 'desc'); }
    setPage(0);
  };
  // заголовок-сортировка (общий для всех колонок таблицы игроков)
  const SortCell = ({ id, label, align = 'right', hint }) => (
    <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align={align}
      sortDirection={sort === id ? dir : false} title={hint || ''}>
      <TableSortLabel active={sort === id} direction={sort === id ? dir : 'desc'}
        onClick={() => sortBy(id)}
        sx={{ '&.Mui-active': { color: '#22e0c8' }, '& .MuiTableSortLabel-icon': { color: '#22e0c8 !important' } }}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  // лайв-обновление списка игроков каждые 5с
  useEffect(() => {
    loadLive();
    const t = setInterval(loadLive, 5000);
    return () => clearInterval(t);
  }, [loadLive]);

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  const num = (v) => Number(v).toLocaleString('ru-RU');
  const usd = (v) => '$' + Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 2 });
  const usd4 = (v) => (v == null ? '—' : '$' + Number(v).toLocaleString('ru-RU', { maximumFractionDigits: 4 }));
  const pct = (v) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`);
  const rate = (v) => (!v ? '—' : `${Number(v).toFixed(1)}/с`);
  // подписанный ± ($) и цвет: зелёный плюс (игрок в плюсе) / красный минус
  const signedUsd = (v) => (v > 0 ? '+' : v < 0 ? '−' : '') + '$' + Number(Math.abs(v || 0)).toLocaleString('ru-RU', { maximumFractionDigits: 2 });
  const netColor = (v) => (v > 0 ? '#2bff9a' : v < 0 ? '#ff4d5e' : '#8fa1c4');
  // §21.07: цвет RTP в строке игрока — видно «в норме / доит / недобирает» без чтения цифр.
  // >105% красный (игрок сверх цели — присмотреться), 85-105% зелёный (норма вокруг 95),
  // ниже — жёлтый (недобрал, вернётся боссами из его owed).
  const rtpColor = (v) => (v == null ? '#5b6b8c' : v > 1.05 ? '#ff4d5e' : v >= 0.85 ? '#2bff9a' : '#f4c34a');

  // §07.09.2026 (владелец): «профит дома — это не профит, если человек крутит ПОДАРОЧНЫЕ».
  // Метка 🎁 у ника = у игрока есть наши бесплатные деньги (реф-подарок / награда за друга /
  // приз лиги). Своих не заносил (depReal = 0) ⇒ метка яркая: весь его «профит дома» — это
  // возврат нашего же подарка, заработком не является.
  const GiftMark = ({ pl }) => {
    if (!(pl.freeTotal > 0)) return null;
    const own = pl.depReal > 0;
    return (
      <Box component="span"
        title={`Подарочных выдано $${pl.freeTotal}${pl.giftAmount > 0 ? ` (тело реф-подарка $${pl.giftAmount})` : ''}`
          + ` · сожжено $${pl.giftBurned} · осталось $${pl.giftLeft}`
          + (own ? ` · своих занёс $${pl.depReal}` : ' · СВОИХ НЕ ЗАНОСИЛ')}
        sx={{ ml: 0.5, px: 0.5, borderRadius: 0.5, fontSize: 10, fontWeight: 800, lineHeight: 1.4,
          color: '#f4c34a', border: '1px solid #f4c34a88', bgcolor: own ? '#f4c34a18' : '#f4c34a33' }}>
        🎁{own ? '' : ' только подарок'}
      </Box>
    );
  };

  // Метка возле ника: 🧪ТЕСТ — тест-сессия (боевое демо, RTP настоящий); D — обычное демо.
  const DMark = ({ demo, forceDemo }) => (
    forceDemo
      ? <Box component="span" sx={{ ml: 0.5, px: 0.5, borderRadius: 0.5, fontSize: 10, fontWeight: 800, lineHeight: 1.4, color: '#5fe8ff', border: '1px solid #5fe8ff88', bgcolor: '#5fe8ff22' }}>🧪ТЕСТ</Box>
      : demo
        ? <Box component="span" sx={{ ml: 0.5, px: 0.5, borderRadius: 0.5, fontSize: 10, fontWeight: 800, lineHeight: 1.4, color: '#f5c945', border: '1px solid #f5c94588', bgcolor: '#f5c94522' }}>D</Box>
        : null
  );

  const idleLabel = (sec) => (sec < 60 ? 'сейчас' : `${Math.floor(sec / 60)} мин назад`);

  // дата обновления леджера — МСК, формат ДД.ММ ЧЧ:ММ
  const fmtDT = (iso) => {
    const d = new Date(iso);
    const msk = { timeZone: 'Europe/Moscow' };
    const day = d.toLocaleDateString('ru-RU', { ...msk, day: '2-digit', month: '2-digit' });
    const time = d.toLocaleTimeString('ru-RU', { ...msk, hour: '2-digit', minute: '2-digit', hour12: false });
    return `${day} ${time}`;
  };
  // время добива для ленты боссов — МСК ЧЧ:ММ:СС
  const fmtTime = (iso) => new Date(iso).toLocaleTimeString('ru-RU', { timeZone: 'Europe/Moscow', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

  const p = stats ? stats[period] : null;

  // §ЛАЙВ-ПУЛЬС: агрегат по онлайну для шапки live-карточки
  const lp = (live && live.players) ? live.players : [];
  const onlineReal = lp.filter((x) => !x.demo).length;
  const onlineDemo = lp.filter((x) => x.demo).length;
  const sumRate = lp.reduce((s, x) => s + (Number(x.rate) || 0), 0);
  const sumNet10 = lp.filter((x) => !x.demo || x.forceDemo).reduce((s, x) => s + (Number(x.net10s) || 0), 0);
  // «в руках сейчас»: сколько онлайн-игроков держат каждую пушку (реал+демо — это про оружие, не деньги)
  const weaponMix = WEAPON_ORDER
    .map((id) => ({ id, n: lp.filter((x) => x.weapon === id).length }))
    .filter((w) => w.n > 0)
    .sort((a, b) => b.n - a.n);

  const wp = weapons ? weapons[period] : null; // популярность оружия за выбранный период

  // §RTP «ПО ГОЛОВАМ» (владелец 20.07): простое среднее RTP игроков (оборот ≥ $1),
  // в отличие от «по кассе» (p.rtp = выплачено/поставлено, ВЗВЕШЕНО по обороту — кит
  // весит больше). Каждый игрок весит одинаково ⇒ число выше, кит с низким RTP не давит.
  // §21.07: считает СЕРВЕР по ВСЕЙ выборке периода (с пагинацией счёт по странице врал бы).
  const psum = pmeta?.summary || null;

  const PERIOD_LABEL = { today: 'сегодня с 00:00 МСК', d7: '7 дней', d30: 'месяц', all: 'всё время' };

  return (
    // §ТЗ владельца 24.07: цифры в лайве прыгали и таскали за собой ширину колонок.
    // tabular-nums = все цифры одной ширины (0 такой же ширины, как 8) ⇒ число меняется,
    // а место под него — нет. Наследуется всей страницей, включая таблицы и карточки.
    // p: {xs:0.5, md:1} — на телефоне поля уже, таблицам достаётся больше ширины.
    <Box sx={{ p: { xs: 0.5, md: 1 }, fontVariantNumeric: 'tabular-nums',
      '& .MuiTableCell-root': { fontVariantNumeric: 'tabular-nums' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <GpsFixed sx={{ color: '#ff7ad1' }} />
        <Typography variant="h6" sx={{ fontWeight: 800 }}>Sugar Strike — статистика</Typography>
        <Chip size="small" label="USDT" sx={{ ml: 1, bgcolor: '#ff7ad122', color: '#ff7ad1' }} />
        <Button size="small" startIcon={<Refresh />} onClick={() => { loadStats(); loadPlayers(); loadLive(); loadWeapons(); loadBossFeed(); }} sx={{ ml: 'auto' }}>Обновить</Button>
      </Box>

      {/* §22.07 (владелец): ЛАЙВ — первым блоком страницы, остальные графы ниже. */}
      <Card sx={{ bgcolor: '#0e1424', border: '1px solid rgba(34,224,200,0.25)', mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          {/* Шапка-пульс: онлайн (реал/демо) · суммарный темп · суммарный ± за 10с */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2bff9a', boxShadow: '0 0 8px #2bff9a' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mr: 1 }}>Лайв — кто в игре</Typography>
            <Chip size="small" label={`👥 онлайн ${lp.length}`} sx={{ bgcolor: '#2bff9a22', color: '#2bff9a', fontWeight: 700 }} />
            <Chip size="small" label={`${onlineReal} реал / ${onlineDemo} демо`} sx={{ bgcolor: '#ffffff10', color: '#8fa1c4' }} />
            {lp.length > 0 && <Chip size="small" label={`⚡ темп ${sumRate.toFixed(1)}/с`} sx={{ bgcolor: '#22e0c822', color: '#22e0c8' }} />}
            {onlineReal > 0 && (
              <Chip size="small" label={`± за 10с ${signedUsd(sumNet10)}`}
                sx={{ bgcolor: `${netColor(sumNet10)}22`, color: netColor(sumNet10), fontWeight: 700 }} />
            )}
          </Box>

          {/* «В руках сейчас» — какая пушка у скольких онлайн-игроков */}
          {weaponMix.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mb: 1.25 }}>
              <Typography variant="caption" sx={{ color: '#5b6b8c', mr: 0.5 }}>В руках сейчас:</Typography>
              {weaponMix.map((w) => (
                <Chip key={w.id} size="small" title={WEAPONS[w.id]?.name || w.id}
                  label={`${weaponEmoji(w.id)} ${WEAPONS[w.id]?.name || w.id} ×${w.n}`}
                  sx={{ bgcolor: '#ff7ad115', color: '#ffb0e3', border: '1px solid #ff7ad133' }} />
              ))}
            </Box>
          )}

          {(!live || !live.players || live.players.length === 0) ? (
            <Typography variant="caption" sx={{ color: '#5b6b8c' }}>Сейчас никто не играет</Typography>
          ) : (
            <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              {/* §24.07: tableLayout fixed + явные ширины колонок. Без этого браузер
                  пересчитывал ширину под КАЖДОЕ новое число, и таблица дёргалась
                  на каждом обновлении лайва (раз в 5 секунд). */}
              <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 1240,
                '& th, & td': { px: 0.75 } }}>
                <TableHead><TableRow>
                  {/* §22.07 (владелец): порядок в лайве строго — ник, СТАВКА, пожизненный RTP,
                      и только потом всё остальное. Это первое, что он смотрит по онлайну. */}
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap', width: 130 }}>Игрок</TableCell>
                  {/* §24.07 (владелец): тут стояла базовая ставка, а с баланса уходит
                      ставка·costMult (надбавка за мощность пушки, до +50%) — 0.12 на тяжёлой
                      пушке списывается как 0.18. Показываем реальную цену, базу — подписью. */}
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap', width: 108 }} align="right"
                    title="Реальная цена одного выстрела = ставка × надбавка за мощность пушки (до +50%)">Цена выстрела</TableCell>
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap', width: 92 }} align="right">RTP ВСЕГО</TableCell>
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap', width: 104 }} align="right">Игрок ± всего</TableCell>
                  {/* §24.07 (владелец): owed — сколько дом ДОЛЖЕН игроку, вернётся боссами (буфер) */}
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap', width: 100 }} align="right" title="Сколько дом должен игроку — вернётся боссами (буфер owed). Положительный = занос впереди.">Долг (буфер)</TableCell>
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap', width: 132 }}>Оружие</TableCell>
                  {/* §ТЗ владельца 24.07 «сколько мне ещё до босса»: остаток порога. Секунды
                      от пушки не зависят, выстрелы зависят — показываем и то и то. */}
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap', width: 104 }} align="right"
                    title="Сколько осталось до следующего босса: выстрелов текущей пушкой и секунд стрельбы">До босса</TableCell>
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap', width: 88 }} align="right">± за 10с</TableCell>
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap', width: 74 }} align="right">Темп</TableCell>
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap', width: 92 }} align="right">Баланс</TableCell>
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap', width: 88 }} align="right">Выстрелов</TableCell>
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap', width: 96 }} align="right">Активность</TableCell>
                  <TableCell sx={{ width: '100%', borderBottom: 'none', p: 0 }} />
                </TableRow></TableHead>
                <TableBody>
                  {live.players.map((pl) => (
                    <TableRow key={pl.userId}>
                      <TableCell sx={{ color: '#fff', whiteSpace: 'nowrap' }}>{pl.name}<DMark demo={pl.demo} forceDemo={pl.forceDemo} /></TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontWeight: 800, fontSize: 14, whiteSpace: 'nowrap' }}>
                        {pl.shotCost != null ? usd4(pl.shotCost) : '—'}
                        {pl.costMult > 1 && (
                          <Typography variant="caption" sx={{ display: 'block', color: '#8fa1c4', fontWeight: 500 }}>
                            {usd4(pl.stake)} ×{Number(pl.costMult).toFixed(2)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ color: (pl.demo && !pl.forceDemo) ? '#5b6b8c' : rtpColor(pl.rtpLife), fontWeight: 800, fontSize: 15, whiteSpace: 'nowrap' }}>{(pl.demo && !pl.forceDemo) ? '—' : pct(pl.rtpLife)}</TableCell>
                      <TableCell align="right" sx={{ color: (pl.demo && !pl.forceDemo) ? '#5b6b8c' : netColor(pl.netLife), fontWeight: 800, whiteSpace: 'nowrap' }}>{(pl.demo && !pl.forceDemo) ? '—' : signedUsd(pl.netLife)}</TableCell>
                      {/* §24.07: буфер owed — дом должен игроку (вернётся боссами). Плюс = занос впереди. */}
                      <TableCell align="right" sx={{ color: (pl.demo && !pl.forceDemo) ? '#5b6b8c' : ((pl.owed || 0) > 0 ? '#5fe8ff' : '#8fa1c4'), fontWeight: 700, whiteSpace: 'nowrap' }}>{(pl.demo && !pl.forceDemo) ? '—' : signedUsd(pl.owed)}</TableCell>
                      <TableCell sx={{ color: '#ffb0e3', whiteSpace: 'nowrap' }} title={WEAPONS[pl.weapon]?.name || pl.weapon || ''}>{weaponLabel(pl.weapon)}</TableCell>
                      <TableCell align="right" sx={{ color: '#5fe8ff', fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {pl.bossLeftShots != null ? `${num(pl.bossLeftShots)} выстр` : '—'}
                        <Typography variant="caption" sx={{ display: 'block', color: '#8fa1c4', fontWeight: 500 }}>
                          ~{pl.bossLeftSec} сек
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ color: (pl.demo && !pl.forceDemo) ? '#5b6b8c' : netColor(pl.net10s), fontWeight: 700, whiteSpace: 'nowrap' }}>{(pl.demo && !pl.forceDemo) ? '—' : signedUsd(pl.net10s)}</TableCell>
                      <TableCell sx={{ color: '#22e0c8', whiteSpace: 'nowrap' }} align="right">{rate(pl.rate)}</TableCell>
                      <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align="right">{usd(pl.balance)}</TableCell>
                      <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align="right">{num(pl.shots)}</TableCell>
                      <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align="right">{idleLabel(pl.idleSec)}</TableCell>
                      <TableCell sx={{ borderBottom: 'none', p: 0 }} />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* переключатель периода — данные всех периодов уже пришли одним /stats, перезапрос не нужен */}
      <ToggleButtonGroup size="small" exclusive value={period} onChange={(e, v) => v && setPeriod(v)} sx={{ mb: 1.5 }}>
        <ToggleButton value="today">Сегодня</ToggleButton>
        <ToggleButton value="d7">7 дней</ToggleButton>
        <ToggleButton value="d30">Месяц</ToggleButton>
        <ToggleButton value="all">Всё время</ToggleButton>
      </ToggleButtonGroup>

      {p && (
        <>
          <Grid container spacing={1.5} sx={{ mb: 1 }}>
            {/* §22.07 (владелец): ПЕРВОЙ цифрой — пожизненный RTP по всей игре. Он не зависит
                от выбранного периода и считается из одного леджера, поэтому сходится с деньгами. */}
            {/* §22.07: главная цифра — на всю ширину телефона, её ищут глазами первой */}
            <Grid item xs={12} md={3}><Stat label="RTP ВСЕГО (факт)" value={pct(psum?.rtpLifeAll)} color="#22e0c8" hint={psum ? `все выплаты $${psum.payoutLife} / все ставки $${psum.turnoverLife} · мини-джекпот $${psum.poolLife} уже внутри · долг/буфер НЕ входит` : 'нет данных'} /></Grid>
            <Grid item xs={6} md={3}><Stat label="Выстрелы" value={num(p.shots)} /></Grid>
            <Grid item xs={6} md={3}><Stat label="Поставлено $" value={usd(p.wager)} /></Grid>
            <Grid item xs={6} md={3}><Stat label="Выплачено $" value={usd(p.payout)} /></Grid>
            <Grid item xs={6} md={3}><Stat label="Профит дома $" value={usd(p.profit)} color={p.profit >= 0 ? '#2bff9a' : '#ff4d5e'} hint="поставлено − выплачено" /></Grid>
            {/* §ТЗ владельца 24.07 «поменьше характеристик, всё разбросано»: убраны три лишних
                RTP — «по кассе», «ср. по игрокам» и «ожидаемый». Осталась ОДНА цифра-факт
                наверху. Долг/буфер виден отдельной колонкой у каждого игрока. */}
            <Grid item xs={6} md={3}><Stat label="Боссы 🔹/🔺" value={`${p.sideKilled}/${p.sideSpawned} · ${p.topKilled}/${p.topSpawned}`} color="#f4c34a" hint="добито / появилось: боковые · верхние" /></Grid>
          </Grid>
          {/* §22.07 (владелец: «этот текст в моталку, чтобы не занимал много места»):
              пояснение к цифрам свёрнуто под кнопку — открывается по клику, по умолчанию
              страница чистая. Текст не сокращал: он объясняет, почему «по кассе» скачет. */}
          <Box sx={{ mb: 2 }}>
            <Button size="small" onClick={() => setHelpOpen((v) => !v)}
              sx={{ color: '#8fa1c4', textTransform: 'none', fontSize: 12, minWidth: 0, p: 0.5 }}>
              {helpOpen ? '▾' : '▸'} Как читать эти цифры
            </Button>
            {helpOpen && (
          <Typography variant="caption" sx={{ color: '#5b6b8c', display: 'block', mt: 0.5 }}>
            Только реальные USDT. Демо и dev-спавны в стату не входят.
            <b> RTP</b> = выплачено / поставлено, одна цифра-факт (долг/буфер НЕ входит —
            кит с большой ставкой весит больше, поэтому число ниже).
            <b> Ср. по игрокам</b> = простое среднее RTP игроков за тот же период
            (оборот ≥ $1; каждый весит одинаково, кит не давит — поэтому выше).
            <b> Ожидаемый RTP</b> = с учётом кассы (недовыплаченное вернётся боссами) —
            к нему всё сходится, цель 95%. «По кассе» ниже, пока касса не высвободилась.
            <b> Периоды — по календарю МСК</b> («Сегодня» = с 00:00 МСК сегодняшних
            суток, а не за 24 часа). Боссы платят из owed, накопленного за ВСЁ время,
            поэтому на коротком окне «по кассе» скачет: игрок может копить кассу
            неделю на большой ставке и высвободить её за час на маленькой (тогда
            за сутки будет хоть 500%). Правда — «Ожидаемый RTP» и RTP по игрокам.
          </Typography>
            )}
          </Box>
        </>
      )}


      {/* §22.07 ДЖЕКПОТ-ПУЛ — 2% оборота поверх базовых 95%. Владелец: «хочу видеть всю
          информацию: сколько накопилось и кому отдали». Показываем остаток, выданное за всё
          время, номер круга, действующий номинал выдачи, очередь и полную историю выплат. */}
      <Card sx={{ bgcolor: '#0e1424', border: '1px solid rgba(43,255,154,0.25)', mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mr: 1 }}>🎁 Джекпот-пул</Typography>
            {/* §24.07: пулов два — реальный и демо-пул теста. Подписываем, какой показан,
                иначе «пул не растёт» (растёт другой). */}
            {poolInfo?.isTest && (
              <Chip size="small" label="🧪 демо-пул теста" title="Идёт публичный тест (SS_FORCE_DEMO): все ставки кормят изолированный демо-пул. Реальный пул стоит нетронутым."
                sx={{ bgcolor: '#f4c34a22', color: '#f4c34a', fontWeight: 800, mr: 0.5 }} />
            )}
            {poolInfo?.pool && (
              <>
                <Chip size="small" label={`в пуле ${usd(poolInfo.pool.amount)}`} sx={{ bgcolor: '#2bff9a22', color: '#2bff9a', fontWeight: 800 }} />
                <Chip size="small" label={`выдано всего ${usd(poolInfo.pool.paidTotal)}`} sx={{ bgcolor: '#7ad1ff22', color: '#7ad1ff' }} />
                <Chip size="small" label={`круг №${poolInfo.pool.circle}`} sx={{ bgcolor: '#ffb0e322', color: '#ffb0e3' }} />
                {/* §22.07 (владелец): сколько человек вообще прошли условия, сколько уже получили */}
                {poolInfo.participants && (
                  <Chip size="small"
                    label={`участников ${poolInfo.participants.total} · получили ${poolInfo.participants.paid} · ждут ${poolInfo.participants.waiting}`}
                    sx={{ bgcolor: '#22e0c822', color: '#22e0c8', fontWeight: 700 }} />
                )}
                <Chip size="small" label={`выдача ${usd(poolInfo.pool.tierMin)}–${usd(poolInfo.pool.tierMax)}`} sx={{ bgcolor: '#f4c34a22', color: '#f4c34a' }} />
                <Chip size="small" label={`оборот игры ${usd(poolInfo.pool.dailyTurnover)}/сутки`} sx={{ bgcolor: '#ffffff11', color: '#8fa1c4' }} />
              </>
            )}
          </Box>
          {/* §22.07 (владелец: «в сноску»): правила пула свёрнуты — открываются по клику */}
          {poolInfo?.pool && (
            <Box sx={{ mb: 1 }}>
              <Button size="small" onClick={() => setPoolHelpOpen((v) => !v)}
                sx={{ color: '#8fa1c4', textTransform: 'none', fontSize: 12, minWidth: 0, p: 0.5 }}>
                {poolHelpOpen ? '▾' : '▸'} Как работает пул
              </Button>
              {poolHelpOpen && (
            <Typography variant="caption" sx={{ color: '#5b6b8c', display: 'block', mt: 0.5 }}>
              Копится <b>{Math.round(poolInfo.pool.rate * 1000) / 10}%</b> с каждой ставки — это
              деньги ПОВЕРХ базовых 95%, суммарный RTP 97%. Выдаётся на добиве верхнего босса.
              В круг попадают игроки с оборотом от {usd(poolInfo.pool.eligTurnover)} за
              {' '}{poolInfo.pool.eligDays} дней; в одном круге игрок получает один раз, все
              получили — круг закрывается. Сумма выдачи — случайная в диапазоне
              {' '}{usd(poolInfo.pool.tierMin)}–{usd(poolInfo.pool.tierMax)} (диапазон растёт
              вместе с оборотом игры), но не больше остатка пула. Порог срабатывания —
              нижняя граница: пул дорос до {usd(poolInfo.pool.tierMin)} — выдаётся на
              ближайшем добиве верхнего. Потолок в одни руки {usd(poolInfo.pool.hardCap)}.
            </Typography>
              )}
            </Box>
          )}
          {/* Очередь: кто уже наиграл право, но в этом круге ещё не получал */}
          {poolInfo?.waiting?.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#8fa1c4', display: 'block', mb: 0.5 }}>
                Ждут своей очереди в круге №{poolInfo.pool?.circle} ({poolInfo.waiting.length}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {poolInfo.waiting.map((w) => (
                  <Chip key={w.userId} size="small" label={`${w.name} · ${usd(w.turnover)}`}
                    sx={{ bgcolor: '#ffffff0d', color: '#8fa1c4' }} />
                ))}
              </Box>
            </Box>
          )}
          {/* §22.07 Итоги по кругам: сколько участников получило и на какую сумму */}
          {poolInfo?.circles?.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#8fa1c4', display: 'block', mb: 0.5 }}>
                По кругам (получили человек · сумма):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {poolInfo.circles.map((c) => (
                  <Chip key={c.circle} size="small"
                    label={`№${c.circle}: ${c.players} чел · ${usd(c.total)}`}
                    sx={{ bgcolor: c.circle === poolInfo.pool?.circle ? '#2bff9a22' : '#ffffff0d',
                      color: c.circle === poolInfo.pool?.circle ? '#2bff9a' : '#8fa1c4', fontWeight: 700 }} />
                ))}
              </Box>
            </Box>
          )}
          {(!poolInfo || !poolInfo.payouts || poolInfo.payouts.length === 0) ? (
            <Typography variant="caption" sx={{ color: '#5b6b8c' }}>Выплат из пула ещё не было.</Typography>
          ) : (
            <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }}>Когда</TableCell>
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }}>Игрок</TableCell>
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align="right">Выдано $</TableCell>
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align="right">Круг</TableCell>
                  <TableCell sx={{ width: '100%', borderBottom: 'none', p: 0 }} />
                </TableRow></TableHead>
                <TableBody>
                  {poolInfo.payouts.map((k) => (
                    <TableRow key={k.id}>
                      <TableCell sx={{ color: '#5b6b8c', whiteSpace: 'nowrap' }}>{fmtDT(k.ts)}</TableCell>
                      <TableCell sx={{ color: '#fff', whiteSpace: 'nowrap' }}>{k.name}</TableCell>
                      <TableCell align="right" sx={{ color: '#2bff9a', fontWeight: 800, whiteSpace: 'nowrap' }}>{usd(k.amount)}</TableCell>
                      <TableCell align="right" sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }}>№{k.circle}</TableCell>
                      <TableCell sx={{ borderBottom: 'none', p: 0 }} />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* §ТЗ владельца 24.07: игроки ВЫШЕ, лента боссов НИЖЕ. */}
      {/* ИГРОКИ — топ по обороту */}
      <Card sx={{ bgcolor: '#0e1424', border: '1px solid rgba(255,255,255,0.06)', mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          {/* §владелец 21.07: заголовок отражает ТЕКУЩУЮ сортировку + сводка по всей выборке */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Игроки — {SORT_LABEL[sort] || 'сортировка'} {dir === 'desc' ? '↓' : '↑'}
            </Typography>
            <Chip size="small" label={PERIOD_LABEL[period]} sx={{ bgcolor: '#ffffff10', color: '#8fa1c4' }} />
            {psum && <Chip size="small" label={`👤 всего ${num(psum.players)}`} sx={{ bgcolor: '#7ad1ff22', color: '#7ad1ff', fontWeight: 700 }} />}
            {/* §24.07: одна цифра-факт вместо трёх вариантов RTP */}
            {psum && <Chip size="small" label={`RTP ${pct(psum.rtpLifeAll)} (всё выплачено / всё поставлено)`} sx={{ bgcolor: '#22e0c822', color: '#22e0c8' }} />}
            {psum && (
              <Chip size="small" label={`профит дома ${signedUsd(psum.houseProfit)}`}
                sx={{ bgcolor: `${netColor(psum.houseProfit)}22`, color: netColor(psum.houseProfit), fontWeight: 700 }} />
            )}
            {/* §07.09.2026 (владелец): валовый профит дома врёт, пока люди крутят наши же
                подарочные. Рядом — сколько роздано подарков, сколько из них уже вернулось
                в кассу и сколько дом заработал ПО-НАСТОЯЩЕМУ (только с денег игроков). */}
            {psum && psum.freeTotal > 0 && (
              <Chip size="small" label={`🎁 подарочных ${usd(psum.freeTotal)} · сожжено ${usd(psum.giftBurned)}`}
                title="Наши бесплатные деньги в казино-кошельке: тело реф-подарка + награда за друга + призы недельной лиги. «Сожжено» — сколько из них уже вернулось в кассу проигрышем и покупкой модулей."
                sx={{ bgcolor: '#f4c34a22', color: '#f4c34a', fontWeight: 700 }} />
            )}
            {psum && (
              <Chip size="small" label={`💵 реальный профит ${signedUsd(psum.houseReal)}`}
                title={`Заработок дома с ДЕНЕГ ИГРОКОВ: валовый профит минус та часть, что лишь вернула наш подарок. Игроки занесли своих: ${usd(psum.depReal)}`}
                sx={{ bgcolor: `${netColor(psum.houseReal)}22`, color: netColor(psum.houseReal), fontWeight: 800 }} />
            )}
            {psum && psum.giftOnly > 0 && (
              <Chip size="small" label={`🎁 только на подарочные: ${num(psum.giftOnly)}`}
                title="Игроков, у которых есть наши подарочные и НЕТ ни одного своего депозита в казино"
                sx={{ bgcolor: '#f4c34a18', color: '#c9a63c', fontWeight: 700 }} />
            )}
            {psum && psum.flagged > 0 && <Chip size="small" label={`🔴 в плюсе ${num(psum.flagged)}`} sx={{ bgcolor: '#ff4d5e22', color: '#ff4d5e', fontWeight: 700 }} />}
          </Box>
          {players.length === 0 ? (
            <Typography variant="caption" sx={{ color: '#5b6b8c' }}>
              {period === 'all' ? 'Пока никто не играл' : 'За период никто не играл. Разбивка по игрокам ведётся с 21.07 — за более ранние дни смотри вкладку «Всё время».'}
            </Typography>
          ) : (
            <>
            <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              {/* Колонка-спейсер (width:100%) в конце съедает пустоту справа → реальные
                  колонки прижаты влево вплотную к «Игрок». sx width:auto MUI перебивал. */}
              {/* §владелец 21.07 «в КАЖДОЙ строке чётко видеть RTP»: RTP — ВТОРАЯ колонка,
                  сразу за ником. Раньше он был шестым и на телефоне уезжал за край экрана
                  (таблица скроллится вбок) — отсюда «нигде не вижу RTP». Рядом «Игрок ±».
                  Все заголовки кликабельны — сортировка серверная, по всей базе. */}
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  <SortCell id="name" label="Игрок" align="left" />
                  {/* §ТЗ владельца 24.07: ОДИН ФАКТИЧЕСКИЙ RTP везде — всё выплаченное делить
                      на всё поставленное. Мини-джекпот из него БОЛЬШЕ НЕ вычитаем: эти деньги
                      игрок реально получил на баланс. Буфер (долг) в RTP НЕ входит — он ещё
                      не выплачен. Раньше тут стоял «базовый» RTP без пула, и он расходился с
                      лайвом на несколько процентов — отсюда «почему везде разные цифры». */}
                  <SortCell id="rtpLifeAll" label="RTP ВСЕГО" hint="ФАКТ: все выплаты / все ставки за всё время. Сходится с балансом: баланс = старт + (RTP−1)·оборот. Долг/буфер сюда НЕ входит — он ещё не выплачен" />
                  {/* §24.07 (владелец): баланс и долг сразу после RTP — три главные цифры впереди */}
                  <SortCell id="balance" label="Баланс $" hint="текущий игровой баланс (в тест-режиме — демо)" />
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align="right" title="Долг/буфер: сколько дом должен игроку — вернётся боссами">Долг (буфер)</TableCell>
                  {/* §22.07 (владелец): надбавка из пула — ОТДЕЛЬНОЙ цифрой, не внутри RTP */}
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align="right">🎁 из пула</TableCell>
                  {/* §ТЗ владельца 24.07: «зачем две колонки на одно и то же» — оставлена ОДНА,
                      профит дома за всё время. Плюс = дом в плюсе по этому игроку. Колонки
                      «RTP за период» и «Игрок ± $ за период» убраны: период путал, а факт
                      один. Игрок ± = минус этой колонки, второй раз не пишем. */}
                  {/* §07.09.2026 (владелец): «профит дома — это не профит дома, если человек
                      играет на подарочные». Две новые колонки: чьими фишками крутит и сколько
                      уже открутил по вагеру ×20. */}
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align="right" title="🎁 Наши бесплатные деньги в казино-кошельке (реф-подарок + награда за друга + призы лиги) и сколько из них уже сожжено. Внизу — сколько игрок занёс СВОИХ USDT.">🎁 Подарочные</TableCell>
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align="right" title="Отыгрыш подарка ×20: сколько уже открутил из требуемого. Пока не закрыт — подарок заперт и не выводится.">Отыгрыш ×20</TableCell>
                  <SortCell id="houseReal" label="Профит дома $" hint="КРУПНО — реальный заработок дома, только с денег игрока. Золотой строкой ниже — часть валового профита, которая лишь вернула наш подарок (заработком не является). Валовый = сумма двух." />
                  <SortCell id="turnover" label="Поставил $" />
                  <SortCell id="payout" label="Выплачено $" />
                  <SortCell id="shots" label="Выстрелов" />
                  <SortCell id="avgBet" label="Ср. ставка" />
                  <SortCell id="bosses" label="Боссы 🔹/🔺" hint="добито боковых / верхних" />
                  <SortCell id="updated" label="Последний онлайн" hint="когда игрок последний раз играл (по нему сортировка по умолчанию)" />
                  <TableCell sx={{ width: '100%', borderBottom: 'none', p: 0 }} />
                </TableRow></TableHead>
                <TableBody>
                  {players.map((pl) => (
                    <TableRow key={pl.userId} sx={pl.flag ? { bgcolor: 'rgba(255,77,94,0.10)' } : undefined}>
                      <TableCell sx={{ color: '#fff', whiteSpace: 'nowrap' }}>{pl.flag ? '🔴 ' : ''}{pl.name}<GiftMark pl={pl} /></TableCell>
                      {/* §22.07 ПОЖИЗНЕННЫЙ RTP — главная цифра, крупно, всегда первой после ника.
                          Считается из одного леджера (все ставки / все выплаты игрока) ⇒ сходится
                          с балансом и не зависит от выбранного периода. */}
                      <TableCell align="right" sx={{ color: rtpColor(pl.rtpLifeAll), fontWeight: 800, fontSize: 15, whiteSpace: 'nowrap' }}>{pct(pl.rtpLifeAll)}</TableCell>
                      {/* §24.07: баланс + долг (буфер) сразу после RTP */}
                      <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align="right">{usd(pl.balance)}</TableCell>
                      <TableCell align="right" sx={{ color: (pl.owed || 0) > 0 ? '#5fe8ff' : '#8fa1c4', fontWeight: 700, whiteSpace: 'nowrap' }}>{signedUsd(pl.owed)}</TableCell>
                      {/* §22.07: сколько человек получил мини-джекпотом + каким станет RTP с ним */}
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {pl.poolLife > 0
                          ? <Chip size="small" label={`🎁 ${usd(pl.poolLife)}`}
                              sx={{ bgcolor: '#f4c34a22', color: '#f4c34a', fontWeight: 700 }} />
                          : <span style={{ color: '#3a4560' }}>—</span>}
                      </TableCell>
                      {/* §07.09: 🎁 подарочные — сколько дали / сколько сожжено (+ свои деньги) */}
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                        {pl.freeTotal > 0 ? (
                          <>
                            <Box sx={{ color: '#f4c34a', fontWeight: 800 }}>{usd(pl.freeTotal)}</Box>
                            <Box sx={{ color: '#8fa1c4', fontSize: 11 }}>
                              сожжено {usd(pl.giftBurned)}
                              {pl.giftLeft > 0 ? ` · ост. ${usd(pl.giftLeft)}` : ''}
                            </Box>
                          </>
                        ) : <span style={{ color: '#3a4560' }}>—</span>}
                        <Box sx={{ color: pl.depReal > 0 ? '#2bff9a' : '#3a4560', fontSize: 11 }}>
                          {pl.depReal > 0 ? `своих ${usd(pl.depReal)}` : 'своих $0'}
                        </Box>
                      </TableCell>
                      {/* §07.09: отыгрыш подарка ×20 — сколько открутил из требуемого */}
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                        {pl.giftTarget > 0 ? (
                          pl.giftReleased
                            ? <Chip size="small" label="🔓 отыгран" sx={{ bgcolor: '#2bff9a22', color: '#2bff9a', fontWeight: 700 }} />
                            : (
                              <>
                                <Box sx={{ color: '#fff', fontWeight: 700 }}>{usd(pl.giftWagered)} / {usd(pl.giftTarget)}</Box>
                                <Box sx={{ color: pl.giftWagered / pl.giftTarget >= 0.5 ? '#f4c34a' : '#8fa1c4', fontSize: 11 }}>
                                  🔒 {Math.floor((pl.giftWagered / pl.giftTarget) * 100)}%
                                </Box>
                              </>
                            )
                        ) : <span style={{ color: '#3a4560' }}>—</span>}
                      </TableCell>
                      {/* §07.09: профит дома = РЕАЛЬНЫЙ (крупно) + возврат подарка (золотым) */}
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                        <Box sx={{ color: netColor(pl.houseReal || 0), fontWeight: 800 }}>{signedUsd(pl.houseReal || 0)}</Box>
                        {pl.houseGift > 0 && (
                          <Box sx={{ color: '#f4c34a', fontSize: 11 }} title="Эта часть профита лишь вернула наш подарок — не заработок">
                            🎁 возврат {usd(pl.houseGift)}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: '#8fa1c4' }} align="right">{usd(pl.turnover)}</TableCell>
                      <TableCell sx={{ color: '#8fa1c4' }} align="right">{usd(pl.payoutTotal)}</TableCell>
                      <TableCell sx={{ color: '#8fa1c4' }} align="right">{pl.shots != null ? num(pl.shots) : '—'}</TableCell>
                      <TableCell sx={{ color: '#8fa1c4' }} align="right">{usd4(pl.avgBet)}</TableCell>
                      <TableCell align="right" sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }}>🔹{num(pl.bossSide || 0)} / 🔺{num(pl.bossTop || 0)}</TableCell>
                      <TableCell sx={{ color: '#5b6b8c', whiteSpace: 'nowrap' }} align="right">{pl.updatedAt ? fmtDT(pl.updatedAt) : '—'}</TableCell>
                      <TableCell sx={{ borderBottom: 'none', p: 0 }} />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            {/* ПАГИНАЦИЯ — страница считается на сервере, «всего» = все игроки за период */}
            <TablePagination
              component="div"
              count={pmeta?.total || 0}
              page={page}
              onPageChange={(e, v) => setPage(v)}
              rowsPerPage={rows}
              onRowsPerPageChange={(e) => { setRows(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100, 200]}
              labelRowsPerPage="На странице:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
              sx={{ color: '#8fa1c4', '& .MuiSvgIcon-root': { color: '#8fa1c4' } }}
            />
            {/* §22.07 (владелец: «тоже в сноску»): расшифровка колонок свёрнута под кнопку —
                текст нужный, но постоянно занимал пол-экрана под таблицей. */}
            <Button size="small" onClick={() => setColsHelpOpen((v) => !v)}
              sx={{ color: '#8fa1c4', textTransform: 'none', fontSize: 12, minWidth: 0, p: 0.5, mt: 1 }}>
              {colsHelpOpen ? '▾' : '▸'} Что означают колонки
            </Button>
            {colsHelpOpen && (
            <Typography variant="caption" sx={{ color: '#5b6b8c', display: 'block', mt: 0.5 }}>
              Всё по слоту Sugar Strike. <b>Поставил</b> = сумма ставок игрока в SS.
              <b> Выплачено</b> = выиграно игроком в SS. <b>RTP</b> = выплачено / поставил
              (на старте &gt;100% из-за фронт-лоада, потом к 95%; красный &gt;100%).
              <b> Игрок ±</b> = выплачено − поставил: <span style={{ color: '#2bff9a' }}>зелёный + = игрок в плюсе</span> (выиграл у дома),
              <span style={{ color: '#ff4d5e' }}> красный − = в минусе</span> (проиграл дому).
              <b> Ср. ставка</b> = оборот / выстрелов (средняя цена выстрела).
              <b> Боссы 🔹/🔺</b> = добито боковых / верхних (копится с 20.07).
              <b> Баланс</b> = текущий USDT игрока (всегда текущий, не за период).
              <b> 🔴</b> у ника = игрок в плюсе на реальном обороте (≥$20) — присмотреться.
              Демо и dev-спавны сюда не входят.
              <b> Профит дома</b> = поставил − выплачено (зеркало «Игрок ±»).
              <b> Период</b> — переключателем вверху страницы, по умолчанию сегодня
              (с 00:00 МСК). «Всё время» — пожизненный итог с первого дня, тут ВСЕ, кто
              когда-либо играл; сегодня/7 дней/месяц считаются по дневным строкам, они
              копятся с 21.07. На коротком окне RTP и «Игрок ±» скачут: боссы платят из
              кассы, накопленной раньше.
              <b> Сортировка</b> — клик по любому заголовку (повторный клик разворачивает).
              Считает сервер по ВСЕЙ базе за период, не по видимой странице.
            </Typography>
            )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ДЕМО — кто играл на виртуальные деньги (владелец 06.09.2026).
          Отдельная таблица и отдельный леджер: в боевую стату демо не пускаем,
          иначе фейковые ставки поехали бы в RTP. */}
      <Card sx={{ bgcolor: '#0e1424', border: '1px solid rgba(255,255,255,0.06)', mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Typography sx={{ color: '#e6ecff', fontWeight: 700 }}>
              🎮 Демо — кто играл
            </Typography>
            <Chip size="small" label={PERIOD_LABEL[period] || period}
              sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: '#8fa1c4' }} />
            <Chip size="small" label={`всего ${demoPlayers?.summary?.players ?? 0}`}
              sx={{ bgcolor: 'rgba(87,148,255,0.15)', color: '#79a8ff', fontWeight: 700 }} />
            {demoPlayers?.summary?.wager > 0 && (
              <Chip size="small"
                label={`оборот $${demoPlayers.summary.wager} · выстрелов ${demoPlayers.summary.shots}`}
                sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: '#8fa1c4' }} />
            )}
          </Box>
          {!demoPlayers?.players?.length ? (
            <Typography variant="caption" sx={{ color: '#5b6b8c' }}>
              За этот период в демо никто не играл.
            </Typography>
          ) : (
            <>
            <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <Table size="small" sx={{ width: 'auto', minWidth: 0 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }}>Игрок</TableCell>
                    <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align="right">Поставил $</TableCell>
                    <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align="right">Выплачено $</TableCell>
                    <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align="right">RTP</TableCell>
                    <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align="right">Игрок ±</TableCell>
                    <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align="right">Выстрелов</TableCell>
                    <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align="right">Ср. ставка</TableCell>
                    <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align="right">Демо-баланс</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {demoPlayers.players.map((p) => (
                    <TableRow key={p.userId} hover>
                      <TableCell sx={{ color: '#e6ecff', whiteSpace: 'nowrap' }}>{p.name}</TableCell>
                      <TableCell sx={{ color: '#c9d6f2' }} align="right">${p.wager}</TableCell>
                      <TableCell sx={{ color: '#c9d6f2' }} align="right">${p.payout}</TableCell>
                      <TableCell align="right"
                        sx={{ color: p.rtp == null ? '#5b6b8c' : p.rtp > 1 ? '#ff4d5e' : '#2bff9a', fontWeight: 700 }}>
                        {p.rtp == null ? '—' : `${(p.rtp * 100).toFixed(1)}%`}
                      </TableCell>
                      <TableCell align="right" sx={{ color: p.playerNet >= 0 ? '#2bff9a' : '#ff4d5e', fontWeight: 700 }}>
                        {p.playerNet >= 0 ? '+' : ''}${p.playerNet}
                      </TableCell>
                      <TableCell sx={{ color: '#c9d6f2' }} align="right">{p.shots}</TableCell>
                      <TableCell sx={{ color: '#8fa1c4' }} align="right">
                        {p.avgBet == null ? '—' : `$${p.avgBet}`}
                      </TableCell>
                      <TableCell sx={{ color: '#8fa1c4' }} align="right">
                        ${p.demoBalance}{p.refills > 0 ? ` · доливок ${p.refills}` : ''}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <Typography variant="caption" sx={{ color: '#5b6b8c', display: 'block', mt: 1 }}>
              Деньги здесь ВИРТУАЛЬНЫЕ. В общий RTP слота и в профит дома эта таблица
              не входит вообще — демо считается отдельным леджером. Смотреть на неё стоит
              как на воронку: сколько людей щупает игру и сколько из них потом заносят.
            </Typography>
            </>
          )}
        </CardContent>
      </Card>

      {/* ОРУЖИЕ — популярность за выбранный период (переключатель периода вверху) */}
      <Card sx={{ bgcolor: '#0e1424', border: '1px solid rgba(255,255,255,0.06)', mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Оружие — популярность</Typography>
            <Chip size="small" label={period === 'today' ? 'сегодня' : period === 'd7' ? '7 дней' : period === 'd30' ? 'месяц' : 'всё время'} sx={{ bgcolor: '#ffffff10', color: '#8fa1c4' }} />
          </Box>
          {(!wp || wp.length === 0) ? (
            <Typography variant="caption" sx={{ color: '#5b6b8c' }}>Пока нет данных за период (копится с момента рестарта бэка).</Typography>
          ) : (
            <>
              {wp.map((w) => (
                <Box key={w.weapon} sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.25 }}>
                    <Typography variant="body2" sx={{ color: '#fff', minWidth: 150 }}>{weaponLabel(w.weapon)}</Typography>
                    <Typography variant="caption" sx={{ color: '#22e0c8', fontWeight: 800 }}>{pct(w.share)}</Typography>
                    <Typography variant="caption" sx={{ color: '#5b6b8c', ml: 'auto' }}>{num(w.shots)} выстр · {num(w.kills)} килов · {usd(w.wager)}</Typography>
                  </Box>
                  <Box sx={{ height: 8, borderRadius: 1, bgcolor: '#ffffff0d', overflow: 'hidden' }}>
                    <Box sx={{ height: '100%', width: `${Math.round((w.share || 0) * 100)}%`, bgcolor: '#ff7ad1', borderRadius: 1 }} />
                  </Box>
                </Box>
              ))}
              <Typography variant="caption" sx={{ color: '#5b6b8c', display: 'block', mt: 1 }}>
                Доля = % выстрелов этой пушкой за период. Только реальные USDT.
                История копится с момента рестарта бэка (до него данных нет).
              </Typography>
            </>
          )}
        </CardContent>
      </Card>      {/* ЛЕНТА БОССОВ — кто какого босса убил и на сколько иксов ЧИСТЫМИ */}
      <Card sx={{ bgcolor: '#0e1424', border: '1px solid rgba(255,122,209,0.22)', mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mr: 1 }}>Лента боссов</Typography>
            {bossFeed?.totals && (
              <>
                <Chip size="small" label={`🔹 боковых ${num(bossFeed.totals.side.n)}`} sx={{ bgcolor: '#f4c34a22', color: '#f4c34a' }} />
                <Chip size="small" label={`🔺 верхних ${num(bossFeed.totals.top.n)}`} sx={{ bgcolor: '#ff7ad122', color: '#ff7ad1' }} />
                <Chip size="small" label={`макс ×${num(Math.max(bossFeed.totals.side.bestMult, bossFeed.totals.top.bestMult))} чистыми`} sx={{ bgcolor: '#2bff9a22', color: '#2bff9a', fontWeight: 700 }} />
              </>
            )}
          </Box>
          {(!bossFeed || !bossFeed.feed || bossFeed.feed.length === 0) ? (
            <Typography variant="caption" sx={{ color: '#5b6b8c' }}>Пока нет добитых боссов (копится с рестарта бэка).</Typography>
          ) : (
            <>
            <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }}>Время</TableCell>
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }}>Игрок</TableCell>
                  {/* §22.07: в ленте сразу видно, кому прилетел занос и какой у него RTP всего */}
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align="right">RTP ВСЕГО</TableCell>
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }}>Босс</TableCell>
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align="right">×N чистыми</TableCell>
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align="right">Занос $</TableCell>
                  {/* §22.07 (владелец): видно, кому сверх заноса упал мини-джекпот из пула */}
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align="right">🎁 Джекпот</TableCell>
                  <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align="right">Ставка</TableCell>
                  <TableCell sx={{ width: '100%', borderBottom: 'none', p: 0 }} />
                </TableRow></TableHead>
                <TableBody>
                  {bossFeed.feed.slice(0, bossShow).map((k) => (
                    // §22.07: строка с мини-джекпотом подсвечена — видно, не вчитываясь в цифры
                    <TableRow key={k.id} sx={k.poolBonus > 0 ? { bgcolor: '#f4c34a12' } : undefined}>
                      <TableCell sx={{ color: '#5b6b8c', whiteSpace: 'nowrap' }}>{fmtTime(k.ts)}</TableCell>
                      <TableCell sx={{ color: '#fff', whiteSpace: 'nowrap' }}>{k.name}</TableCell>
                      <TableCell align="right" sx={{ color: rtpColor(k.rtpLife), fontWeight: 800, whiteSpace: 'nowrap' }}>{pct(k.rtpLife)}</TableCell>
                      <TableCell sx={{ color: k.tier === 'top' ? '#ff7ad1' : '#f4c34a', whiteSpace: 'nowrap' }}>{bossLabel(k.tier, k.bossType)}</TableCell>
                      <TableCell align="right" sx={{ color: '#2bff9a', fontWeight: 800, whiteSpace: 'nowrap' }}>×{num(k.netMult)}</TableCell>
                      <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align="right">{usd(k.net)}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {k.poolBonus > 0
                          ? <Chip size="small" label={`🎁 +${usd(k.poolBonus)}`}
                              sx={{ bgcolor: '#f4c34a22', color: '#f4c34a', fontWeight: 800 }} />
                          : <span style={{ color: '#3a4560' }}>—</span>}
                      </TableCell>
                      <TableCell sx={{ color: '#8fa1c4', whiteSpace: 'nowrap' }} align="right">{usd4(k.stake)}</TableCell>
                      <TableCell sx={{ borderBottom: 'none', p: 0 }} />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            {bossFeed.feed.length > bossShow && (
              <Button size="small" onClick={() => setBossShow((v) => v + 15)}
                sx={{ color: '#5fe8ff', textTransform: 'none', fontSize: 12, mt: 1 }}>
                Показать ещё 15 (осталось {bossFeed.feed.length - bossShow})
              </Button>
            )}
            </>
          )}
          {/* §22.07 (владелец: «в сноску»): легенда ленты свёрнута */}
          <Button size="small" onClick={() => setBossHelpOpen((v) => !v)}
            sx={{ color: '#8fa1c4', textTransform: 'none', fontSize: 12, minWidth: 0, p: 0.5, mt: 1 }}>
            {bossHelpOpen ? '▾' : '▸'} Что значат цифры в ленте
          </Button>
          {bossHelpOpen && (
          <Typography variant="caption" sx={{ color: '#5b6b8c', display: 'block', mt: 0.5 }}>
            <b>×N чистыми</b> = занос / ставку (иксы СВЕРХ вложенного в бой).
            <b> Занос</b> = чистая прибавка к балансу ($). 🔹 боковой · 🔺 верхний.
            <b> 🎁 Джекпот</b> = сколько сверх заноса прилетело из пула этому игроку
            (строка подсвечена). В коопе тут доля добившего — остальные доли того же
            добива смотри в истории выплат пула выше. Только реальные USDT.
            Копится с рестарта бэка 20.07.
          </Typography>
          )}
        </CardContent>
      </Card>


    </Box>
  );
}
